import React from 'react';

declare global {
  interface Window {
    PDFViewerApplication?: any;
  }
}

type PDFViewerProps = {
  src: PDFViewerState;
};

type PDFViewerState = {
    url: string;
    query: string;
    print: string;
    page: string;
};

export default class PDFViewer extends React.Component<PDFViewerProps> {
  private viewerRef: React.RefObject<HTMLDivElement>;
  private src: PDFViewerState;
  constructor(props: PDFViewerProps) {
    super(props);
    this.src = this.props.src;
    this.viewerRef = React.createRef();
  }

  componentDidMount() {
    const element = this.viewerRef.current;
    const iframe = document.createElement('iframe');

    const print = this.src.print;
    if (print === 'true') {
      let printNotified = false;
      // Set once pdf.js's performPrint() runs; an afterprint before that is pdf.js aborting, not a print.
      let nativePrintStarted = false;
      let ownsLock = false;
      // Distinct from ownsLock: stays false in the fail-open (no sessionStorage) path even
      // though that instance is still the active one driving the print flow, but flips true the
      // moment a real owner's heartbeat discovers its lease was stolen by a newer claimant, or
      // the overall timeout fires.
      let abandoned = false;
      let leaseHeartbeat: ReturnType<typeof setInterval> | undefined;
      let overallTimeoutId: ReturnType<typeof setTimeout> | undefined;
      let printWhenReadyId: ReturnType<typeof setInterval> | undefined;
      let awaitNativePrintPhaseId: ReturnType<typeof setInterval> | undefined;
      const printLockKey = 'docrio-preview-print-lock';
      const LEASE_DURATION_MS = 30000;
      const HEARTBEAT_INTERVAL_MS = 10000;
      const MAX_OVERALL_WAIT_MS = 5 * 60 * 1000;
      // Identity only, not the lease timestamp - the stored value's timestamp gets rewritten
      // on every heartbeat below, so ownership has to be checked by this id, not by an exact
      // match against a value captured once at claim time.
      const lockOwnerId = Math.random().toString(36).slice(2);
      // Unconditional - only ever called once, at claim time, right after verifying no one
      // else currently holds a fresh lease. Deliberately no try/catch here: this must throw
      // into the claim block's own catch below, so a write failure is treated as fail-open
      // (ownsLock/heartbeat never start) instead of silently proceeding as if the lease had
      // been written - the heartbeat would otherwise see no lease, mark this instance
      // abandoned, and suppress notifyParent() even though it's the one actually printing.
      const writeLease = () => {
        sessionStorage.setItem(printLockKey, `${Date.now()}:${lockOwnerId}`);
      };
      const clearOwnerTimers = () => {
        if (leaseHeartbeat !== undefined) {
          clearInterval(leaseHeartbeat);
          leaseHeartbeat = undefined;
        }
        if (overallTimeoutId !== undefined) {
          clearTimeout(overallTimeoutId);
          overallTimeoutId = undefined;
        }
        if (printWhenReadyId !== undefined) {
          clearInterval(printWhenReadyId);
          printWhenReadyId = undefined;
        }
        if (awaitNativePrintPhaseId !== undefined) {
          clearInterval(awaitNativePrintPhaseId);
          awaitNativePrintPhaseId = undefined;
        }
      };
      // On-demand version of the check the heartbeat performs periodically - used right before
      // any action a losing instance must never take (printing, or notifying the parent),
      // instead of relying solely on the next heartbeat tick. The heartbeat alone can be stale:
      // if it's delayed (main thread blocked while the native dialog renders, tab backgrounded),
      // another instance can reclaim this lease and start its own print flow before this one's
      // heartbeat gets a chance to notice and flip `abandoned`. Re-reading sessionStorage here
      // closes that race regardless of timer ordering.
      const stillOwnsLease = () => {
        if (abandoned) return false;
        if (!ownsLock) return true; // fail-open instance never held a trackable lease to lose
        try {
          if (sessionStorage.getItem(printLockKey)?.endsWith(`:${lockOwnerId}`)) {
            return true;
          }
        } catch {
          return true; // best-effort; a transient read failure shouldn't abandon a real owner
        }
        ownsLock = false;
        abandoned = true;
        clearOwnerTimers();
        return false;
      };
      // Used by the heartbeat instead of writeLease(). If this callback is delayed past the
      // lease window (tab backgrounded/throttled, main thread blocked), another instance can
      // claim the now-stale lease before this one resumes; blindly rewriting it here would
      // stomp that newer claim and leave both instances believing they own it. Verify the
      // stored owner is still this instance before refreshing, and if it isn't, stop refreshing
      // and abandon any print work that hasn't opened a native dialog yet.
      const refreshLeaseIfOwned = () => {
        if (!stillOwnsLease()) return;
        try {
          sessionStorage.setItem(printLockKey, `${Date.now()}:${lockOwnerId}`);
        } catch {
          // Best-effort only.
        }
      };
      // Tells the host the print flow is done so its spinner clears. Kept separate from
      // releaseLease() since each call site below decides independently whether it's also
      // safe to let another instance claim the lease at that same moment. Guarded by
      // stillOwnsLease() rather than just the abandoned flag, so a stale call that fires before
      // this instance's own heartbeat notices the lease was reclaimed still can't consume the
      // host's one-shot completion listener meant for the new owner - every completion path
      // (onafterprint, the optimistic notify, the overall timeout, the print() error path)
      // funnels through here, so this single check covers all of them.
      // 'Timeout' means no print dialog ever opened, so the host must not record a print.
      const notifyParent = (result: 'Complete' | 'Timeout' = 'Complete') => {
        if (printNotified || !stillOwnsLease()) return;
        printNotified = true;
        window.parent.postMessage({ 'Print': result }, '*');
      };
      // Called on genuine completion/abandonment (real afterprint, the overall timeout, a
      // print() call that failed outright, or the optimistic post-render signal above, which
      // releases here too rather than waiting on the overall timeout - see that call site).
      const releaseLease = () => {
        clearOwnerTimers();
        if (!ownsLock) return;
        try {
          if (sessionStorage.getItem(printLockKey)?.endsWith(`:${lockOwnerId}`)) {
            sessionStorage.removeItem(printLockKey);
          }
        } catch {
          // Best-effort cleanup only; a blocked sessionStorage shouldn't stop the completion signal.
        }
      };
      // An afterprint before pdf.js started the print means it gave up, so report that now instead of waiting out the timeout.
      const completePrint = () => {
        notifyParent(nativePrintStarted ? 'Complete' : 'Timeout');
        releaseLease();
      };
      /**
       * Cancels the pdf.js print job that is still rendering, so its dialog can't open after the
       * host was told the print timed out.
       *
       * Dispatches the same custom `afterprint` event pdf.js's own abort uses; its listener
       * destroys the pending print service and the in-flight render rejects. Call only after
       * `notifyParent('Timeout')` - `printNotified` then stops this synthetic `afterprint` from
       * reporting `'Complete'`. No-op when no print service exists or the iframe is gone.
       */
      const cancelPendingPrint = () => {
        try {
          if (iframe.contentWindow?.PDFViewerApplication?.printService) {
            iframe.contentWindow.dispatchEvent(new CustomEvent('afterprint', { detail: 'custom' }));
          }
        } catch {
          // Best-effort; the iframe may already be gone.
        }
      };
      // Same-origin sibling iframes in this tab share sessionStorage. Production logs showed
      // two full PDFViewer instances running the automated print flow at once (duplicated
      // "Printing PDF..." logs, two native dialogs) - each instance has its own isolated JS
      // state, so a per-iframe guard alone can't stop that. Only the first instance to claim
      // this lease gets to call print(); any other stays completely silent (see below) rather
      // than notifying the parent. Not keyed by URL, since duplicate instances may be given
      // differently-signed URLs for the same underlying document. The lease is refreshed on a
      // heartbeat while this instance is active, since PDF loading/print rendering can
      // legitimately take longer than one lease window - only an owner that's gone dark for a
      // full lease period (crashed/abandoned) is treated as stale. Access is best-effort: if
      // sessionStorage throws (e.g. a sandboxed iframe), fall back to printing normally rather
      // than breaking the viewer entirely.
      //
      // Web Locks was tried here for atomic cross-instance coordination, but this component runs
      // inside a Salesforce Lightning iframe in production - window.top there is cross-origin, so
      // reading anything off it (needed to scope a lock name to this tab) always throws, and
      // every real print would silently fall back to this same sessionStorage path anyway. Kept
      // to just this one mechanism rather than carrying dead code that never runs in production.
      let alreadyClaimedByAnotherInstance = false;
      try {
        const existing = sessionStorage.getItem(printLockKey);
        const existingClaimedAt = existing ? Number(existing.split(':')[0]) : 0;
        alreadyClaimedByAnotherInstance = Date.now() - existingClaimedAt < LEASE_DURATION_MS;
        if (!alreadyClaimedByAnotherInstance) {
          writeLease();
          ownsLock = true;
          leaseHeartbeat = setInterval(refreshLeaseIfOwned, HEARTBEAT_INTERVAL_MS);
        }
      } catch {
        // sessionStorage access failed outright (e.g. a sandboxed iframe, cookies blocked) -
        // fail open and print normally rather than breaking the viewer. ownsLock stays false,
        // so releaseLease() below will skip the sessionStorage write/removal entirely.
        alreadyClaimedByAnotherInstance = false;
      }
      // Armed outside the try, for any instance about to actually run the print flow (a real
      // winner, or the fail-open path above with no lease at all) - but never for a confirmed
      // loser, which must stay silent. Kept outside the try/catch on purpose: a thrown
      // getItem() used to skip this arming entirely, leaving the fail-open path with no bound -
      // an iframe that never loads, or a PDF that never becomes ready, would then never notify
      // the parent and leave the host spinner stuck forever. Also not inside iframe.onload,
      // which may never fire at all (expired signed URL, 404, dropped network); arming it only
      // in onload would leave the heartbeat refreshing this lease for the life of the tab in
      // that case, silently no-oping every later print attempt.
      if (!alreadyClaimedByAnotherInstance) {
        overallTimeoutId = setTimeout(() => {
          console.log('Gave up waiting for the print flow to complete after 5 minutes.');
          notifyParent('Timeout');
          abandoned = true;
          cancelPendingPrint();
          releaseLease();
        }, MAX_OVERALL_WAIT_MS);
      }
      if (alreadyClaimedByAnotherInstance) {
        // Both iframes share the same host window, and the host's print-completion listener
        // gates only on event.origin and consumes itself on the first message it receives -
        // notifying from here would prematurely clear the host's spinner and write its
        // completion record, and would drop the winning instance's real completion signal
        // entirely. Stay silent; this instance still displays the PDF normally below.
        //
        // Known gap: the host's listener is a single, uncorrelated, one-shot subscription per
        // print action (no request id), so if the winner's optimistic notifyParent() below (or
        // any instance) has already fired by the time a later instance mounts, that later
        // instance has no way to be acknowledged at all - it can only ever stay silent here.
        // Fully closing that requires the host to correlate requests (e.g. by id) instead of
        // consuming on the first message; that's a host-side change outside this component.
        console.log('Another print instance already claimed this tab - skipping to avoid a second native dialog.');
      }
      // Always wired, regardless of which instance won the lease, so a losing instance's
      // iframe still gets basic cleanup; only the winner drives the automated print flow.
      iframe.onload = () => {
        if (!iframe?.contentWindow) {
          console.error('iframe contentWindow is not available');
          return;
        }
        iframe.contentWindow.onbeforeunload = () => {
          element?.removeChild(iframe);
        };
        if (alreadyClaimedByAnotherInstance) return;

        iframe.contentWindow.onafterprint = completePrint;
        // pdf.js's own "Preparing document for printing" overlay isn't meant for this fully
        // automated flow. Hide it visually/interactively (not via `display: none`, which evicts
        // an open <dialog> from the browser's top layer and fires a spurious native `close`).
        const style = iframe.contentWindow.document.createElement('style');
        style.textContent = `
          #printServiceDialog, #printServiceDialog::backdrop {
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
        `;
        iframe.contentWindow.document.head.appendChild(style);
        let printInvoked = false;
        const pdfJsPrint = iframe.contentWindow.print.bind(iframe.contentWindow);
        iframe.contentWindow.print = () => {
          if (printInvoked) {
            console.log('Ignoring duplicate print() call; the native dialog already ran once.');
            return;
          }
          printInvoked = true;
          pdfJsPrint();
          // performPrint() runs synchronously on render completion, right before pdf.js
          // schedules the blocking native dialog - the only point a poll can't reliably catch.
          // From here a late 'Timeout' would falsely deny a print that really opens.
          // Wrapped at most once per service: printInvoked gates this block, and a reload gets a fresh window.
          const printService = iframe.contentWindow?.PDFViewerApplication?.printService;
          if (typeof printService?.performPrint === 'function') {
            const performPrint = printService.performPrint.bind(printService);
            printService.performPrint = () => {
              nativePrintStarted = true;
              if (overallTimeoutId !== undefined) {
                clearTimeout(overallTimeoutId);
                overallTimeoutId = undefined;
              }
              return performPrint();
            };
          } else {
            console.warn('pdf.js print service not found; cannot tell when the native print starts.');
            nativePrintStarted = true;
          }
        };
        iframe.click();
        printWhenReadyId = setInterval(() => {
          if (!iframe?.contentWindow) return;
          const app = iframe.contentWindow.PDFViewerApplication;
          // pageViewsReady is `_pages.every(...)`, which is vacuously true before any page views
          // exist, so pagesCount must also be checked to rule out that empty-array startup state.
          const pdfViewer = app?.pdfViewer;
          if (!app?.initialized || !pdfViewer?.pagesCount || !pdfViewer?.pageViewsReady) {
            console.log('PDFViewerApplication not initialized or pages not ready');
            return;
          }
          // Clear before invoking print() (rather than after) so a synchronous exception can
          // never leave this interval running to fire a second, duplicate print() call.
          if (printWhenReadyId !== undefined) {
            clearInterval(printWhenReadyId);
            printWhenReadyId = undefined;
          }
          try {
            if (!stillOwnsLease()) {
              console.log('Lease was reclaimed by another instance before printing - staying silent.');
              return;
            }
            console.log('Printing PDF...');
            iframe.contentWindow.print();
            awaitNativePrintPhaseId = setInterval(() => {
              if (printNotified) {
                if (awaitNativePrintPhaseId !== undefined) {
                  clearInterval(awaitNativePrintPhaseId);
                  awaitNativePrintPhaseId = undefined;
                }
                return;
              }
              const progressBar = iframe?.contentWindow?.document
                .getElementById('printServiceDialog')
                ?.querySelector('progress');
              const renderComplete = app.pdfDocument?.isPureXfa || (progressBar && progressBar.value >= progressBar.max);
              // pdf.js rounds progress to 100% before the last page of a large PDF finishes, so also wait for performPrint().
              if (renderComplete && nativePrintStarted) {
                if (awaitNativePrintPhaseId !== undefined) {
                  clearInterval(awaitNativePrintPhaseId);
                  awaitNativePrintPhaseId = undefined;
                }
                // Cancelling the native print dialog doesn't reliably deliver `afterprint` back
                // to this iframe's window in every browser, which would otherwise leave the
                // caller's spinner stuck forever. Guarantee the parent is told 10s after
                // rendering finishes, and release the lease at the same time rather than
                // waiting on the 5-minute overall timeout - holding it any longer only blocks a
                // later print in this same tab from ever claiming the lease, so that instance
                // would stay silent too and leave its own host spinner with nothing to clear it.
                setTimeout(completePrint, 10000);
              }
            }, 250);
          } catch (e) {
            console.log('Error printing PDF:', e);
            // Nothing was ever shown to the user here, so it's safe to fully release now -
            // without this, a thrown print() call would leave the spinner and lease stuck.
            notifyParent('Timeout');
            abandoned = true;
            cancelPendingPrint();
            releaseLease();
          }
        }, 1000);
      };
    }
    let query = this.src.query;
    if (query.length > 6) {
      // Handle for AND and OR operators
      query = query.replaceAll(' AND ', '|').replaceAll(' OR ', '|');

      // Handle for NOT operator
      const notIndex = query.indexOf(' NOT ');
      query = notIndex === -1 ? query : query.substring(0, notIndex);

      // Replace quotes, prefix search operator, and parentheses
      query = query.replaceAll(/([()*"])/g, '');
    }
    const page = this.src.page;

    iframe.src = `/pdfjs-4.10.38-legacy-dist/web/viewer.html?file=${ encodeURIComponent(this.src.url) }&search=${encodeURIComponent(query)}#page=${page}`;
    iframe.width = '100%';
    iframe.height = '100%';
    element?.appendChild(iframe);
  }

  render() {
    return (
      <div ref={this.viewerRef} id='viewer' style={{ width: '100%', height: '100%' }}>
      </div>
    );
  }

}
